import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Pagination
} from "@mui/material";
import { styled } from '@mui/material/styles';
import BlogCard from "../components/Blog/BlogCard";
import AdBanner from "../components/Ads/AdBanner";
import CategoryNav from "../components/Home/CategoryNav";
import PageHeader from "../components/Layout/HeaderContent";
import { blogService, categoryService } from "../api/services";
import { safeArray } from "../utils/validation";

const CategoryHero = styled(Box)(({ theme, bgcolor }) => ({
  background: bgcolor || `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: '#ffffff',
  padding: theme.spacing(8, 4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(6),
  position: 'relative',
  overflow: 'hidden',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '400px',
    height: '400px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '50%',
    zIndex: 0,
  },
}));

const CategoryPage = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch all categories for navigation
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoryService.getCategories();
        setCategories(safeArray(res.data));
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // Fetch category details
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const catRes = await categoryService.getCategory(slug);
        setCategory(catRes.data);
      } catch (err) {
        console.error("Error fetching category:", err);
        setError("No se pudo cargar la categoría.");
      }
    };

    if (slug) {
      fetchCategory();
    }
  }, [slug]);

  // Fetch posts with pagination
  const fetchPosts = useCallback(async (pageNum) => {
    try {
      setLoading(true);
      const postsRes = await blogService.getPostsByCategory(slug, pageNum);
      const newPosts = safeArray(postsRes.data.results || postsRes.data);
      
      setPosts(newPosts);
      
      // Calculate total pages if count is available
      if (postsRes.data.count) {
        setTotalPages(Math.ceil(postsRes.data.count / 10)); // Assuming 10 items per page
      }

      // Scroll to top on page change
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError("No se pudieron cargar los artículos.");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (slug) {
      setPage(1); // Reset page when category changes
      fetchPosts(1);
    }
  }, [slug, fetchPosts]);

  useEffect(() => {
    if (slug && page > 1) {
      fetchPosts(page);
    }
  }, [page, slug, fetchPosts]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  if (error && !category) {
    return (
      <>
        <PageHeader position="sticky" sx={{ top: 0, zIndex: 1100 }} />
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <PageHeader position="sticky" sx={{ top: 0, zIndex: 1100 }} />
      
      {/* Category Navigation */}
      <CategoryNav categories={categories} />
      
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, md: 4 } }}>
        {/* Hero Section */}
        {category && (
          <CategoryHero bgcolor={category.color}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h2" 
                fontWeight="900" 
                gutterBottom
                sx={{ 
                  fontSize: { xs: '2rem', md: '3.5rem' },
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {category.name}
              </Typography>
              {category.description && (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2, 
                    opacity: 0.95,
                    maxWidth: '800px'
                  }}
                >
                  {category.description}
                </Typography>
              )}
              <Chip 
                label={`${posts.length} Artículos`} 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(10px)'
                }}
              />
            </Box>
          </CategoryHero>
        )}

        {/* Ad Banner */}
        <AdBanner format="horizontal" placement={`category_${slug}_top`} />

        {/* Posts Grid */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {posts.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <Typography variant="h6" color="text.secondary">
                  No hay artículos en esta categoría aún.
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={4}>
                {posts.map((post, index) => {
                  // Insert ad every 6 posts
                  if (index > 0 && index % 6 === 0) {
                    return (
                      <React.Fragment key={`ad-${index}`}>
                        <Grid item xs={12}>
                          <AdBanner format="horizontal" placement={`category_${slug}_${index}`} />
                        </Grid>
                        <Grid item xs={12} md={6} lg={4} key={post.id}>
                          <BlogCard post={post} />
                        </Grid>
                      </React.Fragment>
                    );
                  }
                  
                  return (
                    <Grid item xs={12} md={6} lg={4} key={post.id}>
                      <BlogCard post={post} />
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {/* Pagination */}
            {posts.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <Pagination 
                  count={totalPages} 
                  page={page} 
                  onChange={handlePageChange} 
                  color="primary" 
                  size="large"
                  showFirstButton 
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Bottom Ad */}
        {posts.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <AdBanner format="horizontal" placement={`category_${slug}_bottom`} />
          </Box>
        )}
      </Container>
    </>
  );
};

export default CategoryPage;

